import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { adminApi } from '@/lib/api'
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2,
  Database,
  FileText
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'

interface KBField {
  name: string
  pgType: string
  weaviateType: string
  vectorize: boolean
  embeddingModel?: string
  filterable: boolean
  jsonPath?: string
  inWeaviate?: boolean // Track if field is synced to Weaviate
}

interface KnowledgeBase {
  id: string
  name: string
  description?: string
  schema: KBField[]
  status: string
  createdAt: string
  updatedAt: string
}

const PG_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'VARCHAR(255)', label: 'Short Text (255)' },
  { value: 'TEXT[]', label: 'Text Array (Tags, Keywords)' },
  { value: 'VARCHAR(255)[]', label: 'Short Text Array' },
  { value: 'INTEGER', label: 'Integer' },
  { value: 'INTEGER[]', label: 'Integer Array' },
  { value: 'BIGINT', label: 'Big Integer' },
  { value: 'NUMERIC', label: 'Decimal' },
  { value: 'NUMERIC[]', label: 'Decimal Array' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'BOOLEAN[]', label: 'Boolean Array' },
  { value: 'DATE', label: 'Date' },
  { value: 'DATE[]', label: 'Date Array' },
  { value: 'TIMESTAMP', label: 'Timestamp' },
  { value: 'TIMESTAMP[]', label: 'Timestamp Array' },
  { value: 'JSONB', label: 'JSON' },
  { value: 'UUID', label: 'UUID' },
  { value: 'UUID[]', label: 'UUID Array' },
  { value: 'IMAGE', label: 'Image (URL/Upload)' },
]

const WEAVIATE_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'text[]', label: 'Text Array (Tags, Keywords)' },
  { value: 'int', label: 'Integer' },
  { value: 'int[]', label: 'Integer Array' },
  { value: 'number', label: 'Number' },
  { value: 'number[]', label: 'Number Array' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'boolean[]', label: 'Boolean Array' },
  { value: 'date', label: 'Date' },
  { value: 'date[]', label: 'Date Array' },
  { value: 'uuid', label: 'UUID' },
  { value: 'uuid[]', label: 'UUID Array' },
  { value: 'object', label: 'Object' },
  { value: 'object[]', label: 'Object Array' },
]

const EMBEDDING_MODELS = [
  { value: 'text-embedding-ada-002', label: 'OpenAI Ada v2' },
  { value: 'text-embedding-3-small', label: 'OpenAI v3 Small' },
  { value: 'text-embedding-3-large', label: 'OpenAI v3 Large' },
]

export function KnowledgeBasePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [kb, setKb] = useState<KnowledgeBase | null>(null)
  const [fields, setFields] = useState<KBField[]>([])
  const [isAddFieldDialogOpen, setIsAddFieldDialogOpen] = useState(false)
  const [newField, setNewField] = useState<KBField>({
    name: '',
    pgType: 'TEXT',
    weaviateType: 'text',
    vectorize: false,
    filterable: false,
  })

  // Load KB details
  useEffect(() => {
    loadKnowledgeBase()
  }, [id])

  const loadKnowledgeBase = async () => {
    try {
      setLoading(true)
      const kbData = await adminApi.getKnowledgeBase(id!)
      setKb(kbData)
      setFields(kbData.schema || [])
    } catch (error) {
      console.error('Failed to load knowledge base:', error)
      toast({
        title: 'Error',
        description: 'Failed to load knowledge base',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSchema = async () => {
    try {
      setSaving(true)
      await adminApi.updateKnowledgeBase(id!, {
        schema: fields
      })
      toast({
        title: 'Success',
        description: 'Schema updated successfully',
      })
      // Reload to get latest data
      await loadKnowledgeBase()
    } catch (error) {
      console.error('Failed to save schema:', error)
      toast({
        title: 'Error',
        description: 'Failed to save schema',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleAddField = () => {
    if (!newField.name) {
      toast({
        title: 'Error',
        description: 'Field name is required',
        variant: 'destructive',
      })
      return
    }

    // Check for duplicate names
    if (fields.some(f => f.name.toLowerCase() === newField.name.toLowerCase())) {
      toast({
        title: 'Error',
        description: 'Field name already exists',
        variant: 'destructive',
      })
      return
    }

    setFields([...fields, { ...newField }])
    setNewField({
      name: '',
      pgType: 'TEXT',
      weaviateType: 'text',
      vectorize: false,
      filterable: false,
    })
    setIsAddFieldDialogOpen(false)
  }

  const handleUpdateField = (index: number, updates: Partial<KBField>) => {
    const updatedFields = [...fields]
    const currentField = updatedFields[index]
    if (currentField) {
      updatedFields[index] = { ...currentField, ...updates }
      setFields(updatedFields)
    }
  }

  const handleDeleteField = (index: number) => {
    const field = fields[index]
    if (field && field.inWeaviate) {
      toast({
        title: 'Warning',
        description: 'This field exists in Weaviate. Deletion will only remove it from the schema definition.',
        variant: 'destructive',
      })
    }
    setFields(fields.filter((_, i) => i !== index))
  }

  const handlePushToWeaviate = async () => {
    try {
      setSaving(true)
      // TODO: Implement actual Weaviate schema push
      toast({
        title: 'Info',
        description: 'Weaviate sync not yet implemented',
      })
    } catch (error) {
      console.error('Failed to push to Weaviate:', error)
      toast({
        title: 'Error',
        description: 'Failed to push schema to Weaviate',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!kb) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Knowledge base not found</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/knowledge-bases')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{kb.name}</h1>
            <p className="text-muted-foreground">{kb.description}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => navigate(`/knowledge-bases/${id}/data`)}
          >
            <FileText className="h-4 w-4 mr-2" />
            View Data
          </Button>
          <Button 
            variant="outline"
            onClick={handlePushToWeaviate}
            disabled={saving}
          >
            <Database className="h-4 w-4 mr-2" />
            Push to Weaviate
          </Button>
          <Button 
            onClick={handleSaveSchema}
            disabled={saving || fields.length === 0}
          >
            <Save className="h-4 w-4 mr-2" />
            Save Schema
          </Button>
        </div>
      </div>

      {/* Schema Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Schema Configuration</CardTitle>
          <CardDescription>
            Define the fields for your knowledge base. Use array types (TEXT[], text[]) for tags, categories, and multi-value fields. Changes are saved locally until you push to Weaviate.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fields.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No fields defined yet. Add your first field to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field Name</TableHead>
                  <TableHead>PostgreSQL Type</TableHead>
                  <TableHead>Weaviate Type</TableHead>
                  <TableHead>Vectorize</TableHead>
                  <TableHead>Filterable</TableHead>
                  <TableHead>Embedding Model</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{field.name}</TableCell>
                    <TableCell>
                      <Select
                        value={field.pgType}
                        onValueChange={(value) => handleUpdateField(index, { pgType: value })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PG_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={field.weaviateType}
                        onValueChange={(value) => handleUpdateField(index, { weaviateType: value })}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WEAVIATE_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={field.vectorize}
                        onCheckedChange={(checked: boolean) => handleUpdateField(index, { 
                          vectorize: checked,
                          embeddingModel: checked ? 'text-embedding-ada-002' : undefined
                        })}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={field.filterable}
                        onCheckedChange={(checked: boolean) => handleUpdateField(index, { filterable: checked })}
                      />
                    </TableCell>
                    <TableCell>
                      {field.vectorize ? (
                        <Select
                          value={field.embeddingModel || 'text-embedding-ada-002'}
                          onValueChange={(value) => handleUpdateField(index, { embeddingModel: value })}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {EMBEDDING_MODELS.map(model => (
                              <SelectItem key={model.value} value={model.value}>
                                {model.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {field.inWeaviate ? (
                        <span className="text-green-600 text-sm">In Weaviate</span>
                      ) : (
                        <span className="text-orange-600 text-sm">Local Only</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteField(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="mt-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsAddFieldDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setNewField({
                  name: 'tags',
                  pgType: 'TEXT[]',
                  weaviateType: 'text[]',
                  vectorize: false,
                  filterable: true,
                })
                setIsAddFieldDialogOpen(true)
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Tags Field
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setNewField({
                  name: 'image',
                  pgType: 'IMAGE',
                  weaviateType: 'text',
                  vectorize: false,
                  filterable: false,
                })
                setIsAddFieldDialogOpen(true)
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Image Field
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add Field Dialog */}
      <Dialog open={isAddFieldDialogOpen} onOpenChange={setIsAddFieldDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Field</DialogTitle>
            <DialogDescription>
              Configure the properties for your new field. Use array types (e.g., TEXT[], text[]) for tags, categories, or multi-value fields.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="field-name">Field Name</Label>
              <Input
                id="field-name"
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                placeholder="field_name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PostgreSQL Type</Label>
                <Select
                  value={newField.pgType}
                  onValueChange={(value) => setNewField({ ...newField, pgType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PG_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Weaviate Type</Label>
                <Select
                  value={newField.weaviateType}
                  onValueChange={(value) => setNewField({ ...newField, weaviateType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEAVIATE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="vectorize">Vectorize this field</Label>
              <Switch
                id="vectorize"
                checked={newField.vectorize}
                onCheckedChange={(checked: boolean) => setNewField({ 
                  ...newField, 
                  vectorize: checked,
                  embeddingModel: checked ? 'text-embedding-ada-002' : undefined
                })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="filterable">Make filterable</Label>
              <Switch
                id="filterable"
                checked={newField.filterable}
                onCheckedChange={(checked: boolean) => setNewField({ ...newField, filterable: checked })}
              />
            </div>
            {newField.vectorize && (
              <div className="space-y-2">
                <Label>Embedding Model</Label>
                <Select
                  value={newField.embeddingModel || 'text-embedding-ada-002'}
                  onValueChange={(value) => setNewField({ ...newField, embeddingModel: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMBEDDING_MODELS.map(model => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddFieldDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddField}>
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}